import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { List } from '@mic3/platform-ui';
import Icon from 'app/components/atoms/Icon/Icon';
import Avatar from 'app/components/molecules/Avatar/Avatar';
import {
    SectionTitle,
    StyledListItem,
    StyledListItemIcon,
    StyledListItemText,
    NoResults,
    SearchIcon,
} from '../GlobalSearchModal.styles';

const FilterValueList = ({ filterName, values, filterConfig, onValueClick, highlightedIndex }) => {
    const highlightedRef = useRef(null);
    
    // Auto-scroll to highlighted item
    useEffect(() => {
        if (highlightedRef.current) {
            highlightedRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [highlightedIndex]);
    
    if (values.length === 0) {
        // Check if this filter allows text input (like entityPrimaryIndexes)
        const allowTextInput = filterConfig?.allowTextInput;
        
        return (
            <>
                <SectionTitle variant="caption">Select {filterName}</SectionTitle>
                <NoResults>
                    <SearchIcon name={allowTextInput ? 'text-box-outline' : 'filter-off-outline'} size="lg" />
                    <p>{allowTextInput ? `Type ${filterName.toLowerCase()} and press Enter` : `No matching ${filterName} found`}</p>
                    <small>{allowTextInput ? 'Enter a value directly in the search box above' : 'Try adjusting your search query'}</small>
                </NoResults>
            </>
        );
    }
    
    return (
        <>
            <SectionTitle variant="caption">Select {filterName}</SectionTitle>
            <List>
                {values.map((value, index) => {
                    if (!value || !filterConfig?.getListItemProps) return null;
                    
                    const listItemProps = filterConfig.getListItemProps(value);
                    if (!listItemProps) return null;
                    
                    const isHighlighted = highlightedIndex === index;
                    
                    return (
                        <StyledListItem
                            key={value.id || index}
                            ref={isHighlighted ? highlightedRef : null}
                            button
                            onClick={() => onValueClick(value)}
                            $highlighted={isHighlighted}
                        >
                            <StyledListItemIcon>
                                {listItemProps.isAvatar ? (
                                    <Avatar
                                        src={listItemProps.avatarSrc}
                                        name={listItemProps.avatarName}
                                        alt={listItemProps.avatarName}
                                        width="24px"
                                        height="24px"
                                        lineHeight="24px"
                                    />
                                ) : (
                                    <Icon
                                        name={listItemProps.icon}
                                        type={listItemProps.iconType}
                                        hexColor={listItemProps.iconColor}
                                        size="md"
                                    />
                                )}
                            </StyledListItemIcon>
                            <StyledListItemText
                                primary={listItemProps.primary}
                                secondary={listItemProps.secondary}
                            />
                        </StyledListItem>
                    );
                }).filter(Boolean)}
            </List>
        </>
    );
}

FilterValueList.propTypes = {
    filterName: PropTypes.string.isRequired,
    values: PropTypes.array.isRequired,
    filterConfig: PropTypes.object,
    onValueClick: PropTypes.func.isRequired,
    highlightedIndex: PropTypes.number,
};

FilterValueList.defaultProps = {
    filterConfig: null,
    highlightedIndex: -1,
};

export default FilterValueList;

